import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select, {
  MultiValue,
  StylesConfig,
  components,
  OptionProps,
  MultiValueProps,
  MultiValueRemoveProps,
  MenuProps,
  MenuListProps,
  InputActionMeta,
} from "react-select";
import { Option } from "../../interface/Option";
import { CustomIcdAxiosInstance } from "../../Network/CustomIcd";

interface CustomIcdOption extends Option {
  isInactive?: boolean;
  is_active?: boolean | null;
  is_deleted?: boolean;
  status?: string;
  [key: string]: any;
}

interface ListofCustomIcdProps {
  onChange: (selected: CustomIcdOption[]) => void;
  clear?: boolean;
  defaultValue?: any;
  qaIdPrefix?: string;
  [key: string]: any;
}

const DEFAULT_QA_PREFIX = "list-of-custom-icd";
const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;
const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return { id, "data-testid": id, "data-qa": id };
};

const asBoolean = (value: any): boolean | null => {
  if (value === true || value === 1 || value === "1" ||
      String(value).trim().toLowerCase() === "true") return true;
  if (value === false || value === 0 || value === "0" ||
      String(value).trim().toLowerCase() === "false") return false;
  return null;
};

// "null" means the backend did not provide status. Do not mistake it for Inactive.
const activeStatus = (item: any): boolean | null => {
  if (!item || typeof item !== "object") return null;
  if (asBoolean(item.is_deleted ?? item.isDeleted) === true) return false;
  if (item.isInactive === true || item.__inactive === true) return false;
  const direct = asBoolean(item.is_active ?? item.isActive ?? item.active);
  const status = String(item.status ?? item.Status ?? "").trim().toLowerCase();
  if (direct === false || status === "inactive" || status === "disabled") return false;
  if (direct === true || status === "active" || status === "enabled") return true;
  return null;
};

const optionId = (item: any) => String(
  item && typeof item === "object"
    ? (item.id ?? item.custom_group_id ?? item.value ?? "")
    : (item ?? "")
).trim();

const normalizeOption = (item: any): CustomIcdOption | null => {
  if (item === undefined || item === null) return null;
  const id = optionId(item);
  if (!id) return null;
  const label = typeof item === "object"
    ? String(item.label ?? item.name ?? item.group_name ?? id)
    : id;
  const status = activeStatus(item);
  return {
    ...(typeof item === "object" ? item : {}),
    value: id,
    label,
    is_active: status,
    isInactive: status === false,
  } as CustomIcdOption;
};

const normalizeOptions = (items: any): CustomIcdOption[] => {
  const list = Array.isArray(items) ? items : items ? [items] : [];
  const seen = new Set<string>();
  return list.map(normalizeOption).filter((item): item is CustomIcdOption => {
    if (!item || seen.has(optionId(item))) return false;
    seen.add(optionId(item));
    return true;
  });
};

const extractCustomGroups = (response: any): any[] => {
  const data = response?.data;
  const candidates = [data?.result, data?.getcustemGroups, data?.getCustomGroup, data?.data, data];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (candidate && typeof candidate === "object") {
      for (const key of ["getcustemGroups", "getCustomGroup", "rows", "result", "data"]) {
        if (Array.isArray(candidate[key])) return candidate[key];
      }
    }
  }
  return [];
};

// Compare by normalized SQL ID and enrich selected chips from the full catalogue.
// This deliberately does NOT remove linked inactive entries.
const reconcileSelection = (
  selected: CustomIcdOption[],
  catalogue: CustomIcdOption[],
): CustomIcdOption[] => {
  const lookup = new Map(catalogue.map(item => [optionId(item), item]));
  return selected.map(item => {
    const match = lookup.get(optionId(item));
    if (!match) return item;
    const status = activeStatus(match);
    return {
      ...item,
      ...match,
      value: optionId(item),
      label: String(match.label || item.label),
      is_active: status,
      isInactive: status === false,
    };
  });
};

const isLocked = (item: CustomIcdOption) =>
  item.isInactive === true || activeStatus(item) === false;

const ListofCustomIcd = ({
  onChange,
  clear,
  defaultValue,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: ListofCustomIcdProps) => {
  const [allOptions, setAllOptions] = useState<CustomIcdOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<CustomIcdOption[]>(
    () => normalizeOptions(defaultValue)
  );
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const loadedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  // Fetch independently of defaultValue: selecting an item must NOT refetch or reset.
  const loadAllOptions = useCallback(async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);
    try {
       // Fetch active and inactive catalogue entries so linked inactive chips can
       // be reconciled even when the default endpoint filters active records.
       const readStatus = async (status: "true" | "false") => {
         let result: CustomIcdOption[] = [];
         for (let page = 1; page <= 100; page++) {
           const response = await CustomIcdAxiosInstance.get("getCustomGroup", {
             params: { is_active: status, page, limit: 100 },
           });
           const entries = normalizeOptions(extractCustomGroups(response));
           result = [...result, ...entries];
           const last = Number(response.data?.pagination?.totalPages ?? 1);
           if (page >= last || entries.length === 0) break;
         }
         return result;
       };
       const [active, inactive] = await Promise.all([readStatus("true"), readStatus("false")]);
       const byId = new Map<string, CustomIcdOption>();
       [...active, ...inactive].forEach(item => byId.set(optionId(item), item));
       setAllOptions(Array.from(byId.values()));
    } catch (error) {
      console.error("Error loading Custom ICD groups:", error);
      loadedRef.current = false; // allow a future mount to retry
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadAllOptions(); }, [loadAllOptions]);

  // Controlled-from-parent defaults (including Edit). Do not add them to the menu.
  // The catalogue can load later; the chip stays selected in the meantime.
  useEffect(() => {
    if (defaultValue === undefined || clear) return;
    setSelectedOptions(reconcileSelection(normalizeOptions(defaultValue), allOptions));
  }, [defaultValue, allOptions, clear]);

  useEffect(() => {
    if (!clear) return;
    setSelectedOptions(previous => previous.filter(isLocked));
    setInputValue("");
  }, [clear]);

  const options = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    return allOptions
      .filter(option => activeStatus(option) === true)
      .filter(option => !query || String(option.label).toLowerCase().includes(query));
  }, [allOptions, inputValue]);

  const handleChange = (selected: MultiValue<CustomIcdOption>) => {
    const proposed = [...selected];
    // Preserve linked inactive groups even on Backspace/remove-value/clear action.
    const keep = selectedOptions.filter(item => isLocked(item) &&
      !proposed.some(candidate => optionId(candidate) === optionId(item)));
    const next = reconcileSelection(normalizeOptions([...proposed, ...keep]), allOptions);
    setSelectedOptions(next);
    onChangeRef.current(next);
  };

  const handleInputChange = (value: string, meta: InputActionMeta) => {
    if (meta.action === "input-change") setInputValue(value);
    return value;
  };

  const customStyles: StylesConfig<CustomIcdOption, true> = {
    control: base => ({ ...base, minHeight: "40px", fontSize: "14px", borderRadius: "8px", cursor: "pointer" }),
    valueContainer: base => ({ ...base, fontSize: "14px" }),
    input: base => ({ ...base, fontSize: "14px" }),
    placeholder: base => ({ ...base, fontSize: "14px", color: "#6B7280" }),
    multiValue: (base, props) => ({
      ...base,
      backgroundColor: isLocked(props.data) ? "#d1d5db" : "#007BFF",
      border: isLocked(props.data) ? "1px solid #94a3b8" : "none",
      borderRadius: "5px",
    }),
    multiValueLabel: (base, props) => ({
      ...base,
      color: isLocked(props.data) ? "#334155" : "#FFFFFF",
      fontWeight: isLocked(props.data) ? 600 : 400,
    }),
    multiValueRemove: (base, props) => ({
      ...base,
      display: isLocked(props.data) ? "none" : "flex",
      color: "#FFFFFF",
      cursor: "pointer",
      ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" },
    }),
    option: (base, { isSelected, isFocused }) => ({
      ...base,
      backgroundColor: isSelected ? "#007BFF" : isFocused ? "#E6F0FA" : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      fontSize: "14px",
      padding: "8px 10px",
      cursor: "pointer",
      pointerEvents: "auto",
    }),
    menu: base => ({ ...base, zIndex: 999999, pointerEvents: "auto" }),
    menuPortal: base => ({ ...base, zIndex: 999999, pointerEvents: "auto" }),
    menuList: base => ({ ...base, maxHeight: 260, overflowY: "auto", pointerEvents: "auto", WebkitOverflowScrolling: "touch" }),
  };

  const Menu = (props: MenuProps<CustomIcdOption, true>) => (
    <components.Menu
      {...props}
      innerProps={{ ...(props.innerProps || {}), ...qaAttrs(qaIdPrefix, "menu") }}
    />
  );

  const MenuList = (props: MenuListProps<CustomIcdOption, true>) => (
    <components.MenuList {...props}>{props.children}</components.MenuList>
  );

  const OptionComponent = (props: OptionProps<CustomIcdOption, true>) => (
    <components.Option
      {...props}
      innerProps={{
        ...props.innerProps,
        ...qaAttrs(qaIdPrefix, `option-${qaSafe(optionId(props.data))}`),
        "data-option-label": props.data.label,
        "data-option-value": props.data.value,
        "data-is-inactive": "false",
      } as any}
    />
  );

  const MultiValueComponent = (props: MultiValueProps<CustomIcdOption, true>) => (
    <div
      {...qaAttrs(qaIdPrefix, `selected-${qaSafe(optionId(props.data))}`)}
      data-selected-label={props.data.label}
      data-selected-value={props.data.value}
      data-is-inactive={isLocked(props.data) ? "true" : "false"}
    >
      <components.MultiValue {...props} />
    </div>
  );

  const MultiValueRemove = (props: MultiValueRemoveProps<CustomIcdOption, true>) =>
    isLocked(props.data) ? null : <components.MultiValueRemove {...props} />;

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select<CustomIcdOption, true>
        isMulti
        isClearable={false}
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        classNamePrefix={selectQaId}
        className={selectQaId}
        aria-label="Custom ICD group selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        onInputChange={handleInputChange}
        placeholder="Search by group name..."
        noOptionsMessage={() => loading ? "Loading..." : "No active custom groups found"}
        styles={customStyles}
        components={{ Menu, MenuList, Option: OptionComponent, MultiValue: MultiValueComponent, MultiValueRemove }}
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        menuPosition="fixed"
        menuPlacement="bottom"
        menuShouldScrollIntoView={false}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        captureMenuScroll={true}
      />
      <span {...qaAttrs(qaIdPrefix, "loading-state")} data-loading={loading ? "true" : "false"} style={{ display: "none" }}>
        {loading ? "Loading" : "Idle"}
      </span>
      <span {...qaAttrs(qaIdPrefix, "selected-count")} data-count={selectedOptions.length} style={{ display: "none" }}>
        {selectedOptions.length}
      </span>
      <span {...qaAttrs(qaIdPrefix, "options-count")} data-count={options.length} style={{ display: "none" }}>
        {options.length}
      </span>
      <span {...qaAttrs(qaIdPrefix, "input-value")} data-value={inputValue} style={{ display: "none" }}>
         {inputValue}
       </span>
    </div>
  );
};

export default ListofCustomIcd;
